import { DocumentPackageSummarySection, Finding } from '@consigli/types';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronDown, FaChevronRight, FaArrowUpRightFromSquare } from 'react-icons/fa6';
import { Link } from 'react-router';

import { SummarySectionAccordion } from './summary-section-accordion';

const OTHERS_SECTION_HEADING = 'others';

interface SummaryAccordionItemProps {
  title: string;
  count: number;
  findingsHref: string;
  sections?: DocumentPackageSummarySection[];
  findingsById?: Map<number, Finding>;
}

export const SummaryAccordionItem: FC<SummaryAccordionItemProps> = ({
  title,
  count,
  findingsHref,
  sections = [],
  findingsById = new Map(),
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const hasContent = sections.length > 0;

  const sortedSections = useMemo(
    () =>
      [...sections].sort((a, b) => {
        const aIsOthers = a.heading.trim().toLowerCase() === OTHERS_SECTION_HEADING;
        const bIsOthers = b.heading.trim().toLowerCase() === OTHERS_SECTION_HEADING;
        if (aIsOthers !== bIsOthers) return aIsOthers ? 1 : -1;
        return a.heading.localeCompare(b.heading);
      }),
    [sections],
  );

  const processedSections = useMemo(
    () =>
      sortedSections.map((section) => {
        const findings = section.findingIds
          .map((id) => findingsById.get(id))
          .filter((f): f is Finding => f !== undefined);
        const headingIsLabel = findings.some((f) =>
          f.labels?.some((l) => l.toLowerCase() === section.heading.toLowerCase()),
        );
        return { section, findings, headingIsLabel };
      }),
    [sortedSections, findingsById],
  );

  return (
    <div className="border border-day-light-2 rounded-lg overflow-hidden bg-day-light-5">
      <div className="flex items-center w-full px-4 py-3">
        <button
          className="flex items-center grow min-w-0 text-left disabled:opacity-100 disabled:cursor-default cursor-pointer"
          onClick={() => setOpen((v) => !v)}
          disabled={!hasContent}
        >
          <span className="mr-2 text-day-neutral-subtle shrink-0 w-4">
            {hasContent && (open ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />)}
          </span>
          <div className="flex items-center gap-3 min-w-0">
            <span className="font-semibold text-day-neutral-dark truncate">{title}</span>
            <span className="inline-flex items-center justify-center min-w-6 h-6 px-1.5 rounded-full bg-day-light-2 text-xs font-medium text-day-neutral-subtle">
              {count}
            </span>
          </div>
        </button>
        <Link
          to={findingsHref}
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1.5 ml-4 text-xs font-medium text-day-primary hover:text-day-neutral-dark hover:underline whitespace-nowrap transition-colors shrink-0"
        >
          {t('summary.view-findings')}
          <FaArrowUpRightFromSquare size={10} />
        </Link>
      </div>

      {open && hasContent && (
        <div className="border-t border-day-light-2">
          <ul className="divide-y divide-day-light-2">
            {processedSections.map(({ section, findings, headingIsLabel }) => (
              <SummarySectionAccordion
                key={section.id}
                heading={section.heading}
                body={section.body}
                findings={findings}
                findingsHref={
                  headingIsLabel
                    ? `${findingsHref}&label=${encodeURIComponent(section.heading)}`
                    : undefined
                }
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
